import { memo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";


// ================================
// PERMISSION CHECKBOX COMPONENT
// ================================
interface PermissionCheckboxProps {
  id: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  label: string;
  disabled?: boolean;
}

// 🚀 PERFORMANCE: Memoized Components for Heavy Renders
const MemoizedPermissionCheckbox = memo(({
  id,
  checked,
  onCheckedChange,
  label,
  disabled = false
}: PermissionCheckboxProps) => {
  // 🔥 BOOLEAN VALIDATION: Ensure checked is strictly boolean
  const isCheckedBoolean = checked === true;

  return (
    <div className="flex items-center space-x-2">
      <Checkbox
        id={id}
        checked={isCheckedBoolean}
        onCheckedChange={(checkedValue) => {
          // 🔥 BOOLEAN CONVERSION: Ensure the callback receives strict boolean
          const booleanValue = checkedValue === true;
          onCheckedChange(booleanValue);
        }}
        disabled={disabled}
      />
      <Label htmlFor={id} className="capitalize text-sm">
        {label}
      </Label>
    </div>
  );
});

MemoizedPermissionCheckbox.displayName = 'MemoizedPermissionCheckbox';

// ================================
// PERMISSION GROUP COMPONENT (MAIN)
// ================================

interface PermissionGroupProps {
  groupKey: string;
  group: any;
  permissions: any;
  onPermissionChange: (module: string, submodule: string, action: string, checked: boolean) => void;
  onSelectAll: (module: string, shouldSelect: boolean) => void;
  areAllSelected: boolean;
  assignedCount: number;
  totalCount: number;
  isEditing?: boolean;
}

// 🚀 PERFORMANCE: Memoized Permission Group Component
export const MemoizedPermissionGroup = memo(({
  groupKey,
  group,
  permissions,
  onPermissionChange,
  onSelectAll,
  areAllSelected,
  assignedCount,
  totalCount,
  isEditing = false
}: PermissionGroupProps) => {
  // Safety check for group prop
  if (!group || typeof group !== 'object') {
    return null;
  }

  return (
    <AccordionItem value={groupKey} className="border rounded-lg">
      <AccordionTrigger className="p-4 hover:no-underline">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-3">
            <span className="font-semibold">{group.description}</span>
            <Badge variant="secondary">
              {assignedCount} / {totalCount} permissions
            </Badge>
          </div>
          <div className="flex items-center gap-2 pr-2">
            <Checkbox
              id={`select-all-${isEditing ? 'edit-' : ''}${groupKey}`}
              checked={areAllSelected === true}
              onCheckedChange={(checked) => {
                // 🔥 BOOLEAN CONVERSION: Ensure strict boolean value
                const booleanValue = checked === true;
                onSelectAll(groupKey, booleanValue);
              }}
            />
            <Label
              htmlFor={`select-all-${isEditing ? 'edit-' : ''}${groupKey}`}
              className="text-sm cursor-pointer select-none"
              tabIndex={0}
              role="button"
              onClick={(event) => {
                event.preventDefault();
                onSelectAll(groupKey, !(areAllSelected === true));
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onSelectAll(groupKey, !(areAllSelected === true));
                }
              }}
            >
              Select All
            </Label>
          </div>
        </div>
      </AccordionTrigger>
      <AccordionContent className="p-4 pt-0">
        <div className="space-y-4 pt-4 border-t">
          {group.modules ? Object.entries(group.modules).map(([moduleKey, moduleName]) => {
            // Handle nested structure (like hiring.settings)
            if (moduleKey === 'settings' && typeof moduleName === 'object' && moduleName !== null) {
              return (
                <Card key={moduleKey}>
                  <CardHeader>
                    <CardTitle className="text-base">Settings</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {Object.entries(moduleName as Record<string, any>).map(([nestedModuleKey, nestedModuleName]) => (
                        <div key={nestedModuleKey} className="border-t pt-4 first:border-t-0 first:pt-0">
                          <h4 className="font-medium text-sm text-gray-700 mb-3">{String(nestedModuleName)}</h4>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {Object.keys(permissions?.[groupKey]?.[moduleKey]?.[nestedModuleKey] || {}).map((action) => {
                              const permissionValue = permissions?.[groupKey]?.[moduleKey]?.[nestedModuleKey]?.[action];
                              // 🔥 BOOLEAN VALIDATION: Ensure strict boolean check
                              const isChecked = permissionValue === true;

                              return (
                                <MemoizedPermissionCheckbox
                                  key={`${groupKey}-${moduleKey}-${nestedModuleKey}-${action}`}
                                  id={`${isEditing ? 'edit-' : ''}${groupKey}-${moduleKey}-${nestedModuleKey}-${action}`}
                                  checked={isChecked}
                                  onCheckedChange={(checked) => {
                                    // 🔥 BOOLEAN CONVERSION: Ensure strict boolean value
                                    const booleanValue = checked === true;
                                    // For nested structure, we need to pass the nested path
                                    onPermissionChange(groupKey, `${moduleKey}.${nestedModuleKey}`, action, booleanValue);
                                  }}
                                  label={action}
                                />
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            } else {
              // Handle regular structure (like hiring.candidate)
              return (
                <Card key={moduleKey}>
                  <CardHeader>
                    <CardTitle className="text-base">{String(moduleName)}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {(() => {
                        // For sales modules (client, proposal, contract), always show all four actions
                        const salesModules = ['client', 'proposal', 'contract'];
                        const isSalesModule = salesModules.includes(moduleKey);

                        // Determine which actions to show
                        let actionsToShow: string[] = [];
                        if (isSalesModule) {
                          // Always show view, create, edit, delete for sales modules
                          actionsToShow = ['view', 'create', 'edit', 'delete'];
                        } else if (moduleKey === 'revenue') {
                          // Revenue Management only shows view action
                          actionsToShow = ['view'];
                        } else {
                          // For other modules, use the actions from permissions object
                          actionsToShow = Object.keys(permissions?.[groupKey]?.[moduleKey] || {});
                        }

                        return actionsToShow.map((action) => {
                          // Filter out create and delete actions for Agreement and Compensation modules
                          if ((moduleKey === 'agreement' || moduleKey === 'compensation') &&
                            (action === 'create' || action === 'delete')) {
                            return null;
                          }

                          // Filter out create, edit, and delete actions for Revenue module
                          if (moduleKey === 'revenue' &&
                            (action === 'create' || action === 'edit' || action === 'delete')) {
                            return null;
                          }

                          const permissionValue = permissions?.[groupKey]?.[moduleKey]?.[action];
                          // 🔥 BOOLEAN VALIDATION: Ensure strict boolean check
                          const isChecked = permissionValue === true;

                          // Special handling for permission module actions
                          let displayLabel = action;
                          if (moduleKey === 'permission') {
                            if (action === 'view') displayLabel = 'View Permissions';
                            else if (action === 'manage') displayLabel = 'Manage Permissions';
                          }

                          return (
                            <MemoizedPermissionCheckbox
                              key={`${groupKey}-${moduleKey}-${action}`}
                              id={`${isEditing ? 'edit-' : ''}${groupKey}-${moduleKey}-${action}`}
                              checked={isChecked}
                              onCheckedChange={(checked) => {
                                // 🔥 BOOLEAN CONVERSION: Ensure strict boolean value
                                const booleanValue = checked === true;
                                onPermissionChange(groupKey, moduleKey, action, booleanValue);
                              }}
                              label={displayLabel}
                            />
                          );
                        });
                      })()}
                    </div>
                  </CardContent>
                </Card>
              );
            }
          }) : (
            <div className="text-center py-8 text-gray-500">
              <p>No modules available for this permission group.</p>
            </div>
          )}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
});

MemoizedPermissionGroup.displayName = 'MemoizedPermissionGroup'; 